import { Building, Floor, Apartment } from '../types/building';
import { Photo } from '../types/photo';
import { 
  getBuildings, 
  getArchivedBuildings, 
  getBuilding, 
  updateBuilding,
  createBuilding,
  deleteBuilding,
  updateApartment
} from './localDatabase';
import { getBuildingPhotos, getApartmentPhotos, deletePhoto } from './photoStorage';
import { triggerWebhook } from './webhooks';

// API version
const API_VERSION = 'v1';

// API key validation
const validateApiKey = (apiKey: string): boolean => {
  // In a real implementation, this would validate against stored API keys
  // For now, we'll accept a simple key for demonstration
  const validApiKeys = localStorage.getItem('installtrack_api_keys');
  if (!validApiKeys) return false;
  
  try {
    const keys = JSON.parse(validApiKeys);
    return keys.some((key: {key: string, active: boolean}) => 
      key.key === apiKey && key.active
    );
  } catch (error) {
    console.error('Error validating API key:', error);
    return false;
  }
};

// Generate a new API key
export const generateApiKey = (): string => {
  const key = `itpro_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
  
  // Store the key
  const existingKeys = localStorage.getItem('installtrack_api_keys');
  let keys = [];
  
  if (existingKeys) {
    try {
      keys = JSON.parse(existingKeys);
    } catch (error) {
      console.error('Error parsing existing API keys:', error);
    }
  }
  
  keys.push({
    key,
    created: new Date().toISOString(),
    active: true,
    lastUsed: null
  });
  
  localStorage.setItem('installtrack_api_keys', JSON.stringify(keys));
  
  return key;
};

// Get all API keys
export const getApiKeys = () => {
  const existingKeys = localStorage.getItem('installtrack_api_keys');
  if (!existingKeys) return [];
  
  try {
    return JSON.parse(existingKeys);
  } catch (error) {
    console.error('Error parsing API keys:', error);
    return [];
  }
};

// Revoke an API key
export const revokeApiKey = (key: string): boolean => {
  const existingKeys = localStorage.getItem('installtrack_api_keys');
  if (!existingKeys) return false;
  
  try {
    const keys = JSON.parse(existingKeys);
    const keyIndex = keys.findIndex((k: {key: string}) => k.key === key);
    
    if (keyIndex === -1) return false;
    
    keys[keyIndex].active = false;
    localStorage.setItem('installtrack_api_keys', JSON.stringify(keys));
    return true;
  } catch (error) {
    console.error('Error revoking API key:', error);
    return false;
  }
};

// Update API key last used timestamp
const updateApiKeyUsage = (apiKey: string) => {
  const existingKeys = localStorage.getItem('installtrack_api_keys');
  if (!existingKeys) return;
  
  try {
    const keys = JSON.parse(existingKeys);
    const keyIndex = keys.findIndex((k: {key: string}) => k.key === key);
    
    if (keyIndex === -1) return;
    
    keys[keyIndex].lastUsed = new Date().toISOString();
    localStorage.setItem('installtrack_api_keys', JSON.stringify(keys));
  } catch (error) {
    console.error('Error updating API key usage:', error);
  }
};

// API response types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  meta?: {
    version: string;
    timestamp: string;
  };
}

// API handler
export const handleApiRequest = async (
  endpoint: string,
  method: string,
  apiKey: string,
  body?: any
): Promise<ApiResponse<any>> => {
  // Validate API key
  if (!validateApiKey(apiKey)) {
    return {
      success: false,
      error: 'Invalid API key',
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // Update API key usage
  updateApiKeyUsage(apiKey);
  
  try {
    // Parse endpoint
    const parts = endpoint.split('/').filter(Boolean);
    
    // Handle different endpoints
    if (parts[0] === 'buildings') {
      return await handleBuildingsEndpoint(parts.slice(1), method, body);
    } else if (parts[0] === 'photos') {
      return await handlePhotosEndpoint(parts.slice(1), method, body);
    } else {
      return {
        success: false,
        error: 'Invalid endpoint',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
  } catch (error) {
    console.error('API error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
};

// Handle buildings endpoints
const handleBuildingsEndpoint = async (
  parts: string[],
  method: string,
  body?: any
): Promise<ApiResponse<any>> => {
  // GET /buildings - List all buildings
  if (parts.length === 0 && method === 'GET') {
    const buildings = getBuildings();
    return {
      success: true,
      data: buildings,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // POST /buildings - Create a new building
  if (parts.length === 0 && method === 'POST') {
    if (!body || !body.name || !body.address) {
      return {
        success: false,
        error: 'Name and address are required',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    const newBuilding = createBuilding(body.name, body.address);
    
    // Trigger webhook
    triggerWebhook('building.created', newBuilding);
    
    return {
      success: true,
      data: newBuilding,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // GET /buildings/archived - List archived buildings
  if (parts[0] === 'archived' && method === 'GET') {
    const buildings = getArchivedBuildings();
    return {
      success: true,
      data: buildings,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // GET /buildings/{id} - Get a specific building
  if (parts.length === 1 && method === 'GET') {
    const buildingId = parts[0];
    const building = getBuilding(buildingId);
    
    if (!building) {
      return {
        success: false,
        error: 'Building not found',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    return {
      success: true,
      data: building,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // PUT /buildings/{id} - Update a building
  if (parts.length === 1 && method === 'PUT') {
    const buildingId = parts[0];
    const building = getBuilding(buildingId);
    
    if (!building) {
      return {
        success: false,
        error: 'Building not found',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    if (!body) {
      return {
        success: false,
        error: 'Request body is required',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    const updatedBuilding = updateBuilding(buildingId, {
      ...building,
      ...body,
      id: buildingId // Ensure ID doesn't change
    });
    
    // Trigger webhook
    triggerWebhook('building.updated', updatedBuilding);
    
    return {
      success: true,
      data: updatedBuilding,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // DELETE /buildings/{id} - Delete a building
  if (parts.length === 1 && method === 'DELETE') {
    const buildingId = parts[0];
    const building = getBuilding(buildingId);
    
    if (!building) {
      return {
        success: false,
        error: 'Building not found',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    deleteBuilding(buildingId);
    
    // Trigger webhook
    triggerWebhook('building.deleted', { id: buildingId });
    
    return {
      success: true,
      data: { id: buildingId },
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // GET /buildings/{id}/photos - Get photos for a building
  if (parts.length === 2 && parts[1] === 'photos' && method === 'GET') {
    const buildingId = parts[0];
    const building = getBuilding(buildingId);
    
    if (!building) {
      return {
        success: false,
        error: 'Building not found',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    const photos = await getBuildingPhotos(buildingId);
    
    return {
      success: true,
      data: photos,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // PUT /buildings/{buildingId}/apartments/{apartmentId} - Update an apartment
  if (parts.length === 3 && parts[1] === 'apartments' && method === 'PUT') {
    const buildingId = parts[0];
    const apartmentId = parts[2];
    
    const building = getBuilding(buildingId);
    if (!building) {
      return {
        success: false,
        error: 'Building not found',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    // Find the floor that contains this apartment
    let floorId: string | null = null;
    let apartment: Apartment | null = null;
    
    for (const floor of building.floors) {
      const apt = floor.apartments.find(a => a.id === apartmentId);
      if (apt) {
        floorId = floor.id;
        apartment = apt;
        break;
      }
    }
    
    if (!floorId || !apartment) {
      return {
        success: false,
        error: 'Apartment not found',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    if (!body) {
      return {
        success: false,
        error: 'Request body is required',
        meta: {
          version: API_VERSION,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    const updatedApartment = updateApartment(buildingId, floorId, apartmentId, {
      ...apartment,
      ...body,
      id: apartmentId // Ensure ID doesn't change
    });
    
    // Trigger webhook
    triggerWebhook('apartment.updated', {
      buildingId,
      floorId,
      apartment: updatedApartment
    });
    
    return {
      success: true,
      data: updatedApartment,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // Endpoint not found
  return {
    success: false,
    error: 'Invalid endpoint or method',
    meta: {
      version: API_VERSION,
      timestamp: new Date().toISOString()
    }
  };
};

// Handle photos endpoints
const handlePhotosEndpoint = async (
  parts: string[],
  method: string,
  body?: any
): Promise<ApiResponse<any>> => {
  // GET /photos/apartment/{id} - Get photos for an apartment
  if (parts.length === 2 && parts[0] === 'apartment' && method === 'GET') {
    const apartmentId = parts[1];
    const photos = await getApartmentPhotos(apartmentId);
    
    return {
      success: true,
      data: photos,
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // DELETE /photos/{id} - Delete a photo
  if (parts.length === 1 && method === 'DELETE') {
    const photoId = parts[0];
    
    await deletePhoto(photoId);
    
    // Trigger webhook
    triggerWebhook('photo.deleted', { id: photoId });
    
    return {
      success: true,
      data: { id: photoId },
      meta: {
        version: API_VERSION,
        timestamp: new Date().toISOString()
      }
    };
  }
  
  // Endpoint not found
  return {
    success: false,
    error: 'Invalid endpoint or method',
    meta: {
      version: API_VERSION,
      timestamp: new Date().toISOString()
    }
  };
};